import React, { useState } from "react";
import { apiService } from "@services/api";
import {
  Box,
  Grid,
  Typography,
  Container,
  TextField,
  Button,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { motion } from "framer-motion";
import { MdAddCall, MdEmail, MdLocationOn } from "react-icons/md";
import contactBg from "@assets/images/contactbg.jpg";
import rentbanner from "@assets/images/istockphoto-3.jpg";
import { toast } from "react-toastify";
import SEO from "../../../utils/SeoComponent";

// Styled Components (reusing some from Services page)
const PageSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(10, 0),
  position: "relative",
  overflow: "hidden",
}));

const HeroSection = styled(PageSection)(({ theme }) => ({
  backgroundColor: "#f8f9fa",
  backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.7)), url(${contactBg})`,
  backgroundSize: "cover",
  backgroundPosition: "center",
  color: "white",
  padding: theme.spacing(15, 0),
  textAlign: "center",
  position: "relative",
  "&::before": {
    content: '""',
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    height: "150px",
    background: "linear-gradient(to top, #fff, transparent)",
  },
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  position: "relative",
  fontWeight: 700,
  marginBottom: theme.spacing(6),
  paddingBottom: theme.spacing(2),
  display: "inline-block",
  "&::after": {
    content: '""',
    position: "absolute",
    bottom: 0,
    left: 0,
    width: "80px",
    height: "4px",
    backgroundColor: "#C10E14",
  },
}));

const ContactFormBox = styled(Box)(({ theme }) => ({
  backgroundColor: "#fff",
  borderRadius: "15px",
  padding: theme.spacing(5),
  boxShadow: "0 10px 30px rgba(0, 0, 0, 0.1)",
  height: "100%",
  [theme.breakpoints.down("md")]: {
    padding: theme.spacing(3),
  },
}));

const ContactInfoBox = styled(Box)(({ theme }) => ({
  backgroundColor: "#f8f9fc",
  borderRadius: "15px",
  padding: theme.spacing(5),
  height: "100%",
  boxShadow: "0 5px 15px rgba(0, 0, 0, 0.05)",
  [theme.breakpoints.down("md")]: {
    padding: theme.spacing(3),
  },
}));

const IconWrapper = styled(Box)(({ theme }) => ({
  marginRight: theme.spacing(2),
  backgroundColor: "#f8f9fc",
  borderRadius: "50%",
  padding: theme.spacing(2),
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "1.5rem",
  color: "#C10E14",
  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
}));

const ContactImage = styled("img")(({ theme }) => ({
  width: "100%",
  borderRadius: "15px",
  boxShadow: "0 15px 35px rgba(0, 0, 0, 0.2)",
  [theme.breakpoints.down("md")]: {
    marginTop: theme.spacing(4),
  },
}));

const Contact = () => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    message: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formDataToSend = new FormData();
    formDataToSend.append("first_name", formData.firstName);
    formDataToSend.append("last_name", formData.lastName);
    formDataToSend.append("email", formData.email);
    formDataToSend.append("message", formData.message);
    try {
      const res = await apiService.submitContact(formDataToSend);
      toast.success("Submitted successfully!");
      setFormData({
        firstName: "",
        lastName: "",
        email: "",
        message: "",
      });
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("Something went wrong!");
    }
  };

  return (
    <>
      <SEO slug={"contactus"}  />
      <Box className="contact-page">
        {/* Hero Section */}
        <HeroSection className="hero-section">
          <Container
            maxWidth="md"
            sx={{
              mt: { xs: 6, md: 14 },
            }}
          >
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <Typography
                variant="h2"
                component="h1"
                gutterBottom
                fontWeight={700}
              >
                Contact Us
              </Typography>
              <Typography
                variant="h5"
                component="p"
                sx={{ mb: 4, opacity: 0.9 }}
              >
                We're here to help with all your real estate needs
              </Typography>
              <Button
                variant="contained"
                size="large"
                onClick={() => {
                  const element = document.getElementById("contact-form");
                  if (element) {
                    element.scrollIntoView({ behavior: "smooth" });
                  }
                }}
                sx={{
                  borderRadius: "50px",
                  py: 1.5,
                  px: 4,
                  fontSize: "1rem",
                  boxShadow: "0 4px 14px rgba(0, 0, 0, 0.25)",
                  backgroundColor: "#C10E14", // Set the background color to #C10E14
                  "&:hover": {
                    backgroundColor: "#A30B11", // Set the hover background color to #A30B11
                  },
                }}
              >
                Get In Touch
              </Button>
            </motion.div>
          </Container>
        </HeroSection>

        {/* Contact Form Section */}
        <PageSection id="contact-form" sx={{ bgcolor: "#f8f9fc" }}>
          <Container>
            <Box sx={{ textAlign: "center", mb: 6 }}>
              <SectionTitle variant="h4" component="h2">
                Get In Touch
              </SectionTitle>
              <Typography
                variant="subtitle1"
                color="text.secondary"
                sx={{ maxWidth: "700px", mx: "auto" }}
              >
                Have questions or ready to start your real estate journey? Reach
                out to our team today.
              </Typography>
            </Box>

            <Grid container spacing={6} alignItems="center">
              <Grid item xs={12} md={6}>
                <motion.div
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8 }}
                >
                  <ContactFormBox component="form" onSubmit={handleSubmit}>
                    <Typography
                      variant="h5"
                      component="h3"
                      gutterBottom
                      fontWeight={600}
                    >
                      Send Us a Message
                    </Typography>
                    <Grid container spacing={3}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          label="First Name"
                          name="firstName"
                          variant="outlined"
                          fullWidth
                          value={formData.firstName}
                          onChange={handleChange}
                          required
                          sx={{
                            mb: 2,
                            "& .MuiInputLabel-root": {
                              color: "black", // label color (unfocused)
                            },
                            "& .MuiInputLabel-root.Mui-focused": {
                              color: "#C10E14", // label color on focus
                            },
                            "& .MuiOutlinedInput-root": {
                              "& fieldset": {
                                borderColor: "black", // default border
                              },
                              "&:hover fieldset": {
                                borderColor: "#C10E14", // hover border
                              },
                              "&.Mui-focused fieldset": {
                                borderColor: "#C10E14", // border on focus
                              },
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          label="Last Name"
                          name="lastName"
                          variant="outlined"
                          fullWidth
                          value={formData.lastName}
                          onChange={handleChange}
                          required
                          sx={{
                            mb: 2,
                            "& .MuiInputLabel-root": {
                              color: "black", // label color (unfocused)
                            },
                            "& .MuiInputLabel-root.Mui-focused": {
                              color: "#C10E14", // label color on focus
                            },
                            "& .MuiOutlinedInput-root": {
                              "& fieldset": {
                                borderColor: "black", // default border
                              },
                              "&:hover fieldset": {
                                borderColor: "#C10E14", // hover border
                              },
                              "&.Mui-focused fieldset": {
                                borderColor: "#C10E14", // border on focus
                              },
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          label="Email"
                          name="email"
                          type="email"
                          variant="outlined"
                          fullWidth
                          value={formData.email}
                          onChange={handleChange}
                          required
                          sx={{
                            mb: 2,
                            "& .MuiInputLabel-root": {
                              color: "black", // label color (unfocused)
                            },
                            "& .MuiInputLabel-root.Mui-focused": {
                              color: "#C10E14", // label color on focus
                            },
                            "& .MuiOutlinedInput-root": {
                              "& fieldset": {
                                borderColor: "black", // default border
                              },
                              "&:hover fieldset": {
                                borderColor: "#C10E14", // hover border
                              },
                              "&.Mui-focused fieldset": {
                                borderColor: "#C10E14", // border on focus
                              },
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          label="Message"
                          name="message"
                          variant="outlined"
                          fullWidth
                          multiline
                          rows={4}
                          value={formData.message}
                          onChange={handleChange}
                          required
                          sx={{
                            mb: 2,
                            "& .MuiInputLabel-root": {
                              color: "black", // label color (unfocused)
                            },
                            "& .MuiInputLabel-root.Mui-focused": {
                              color: "#C10E14", // label color on focus
                            },
                            "& .MuiOutlinedInput-root": {
                              "& fieldset": {
                                borderColor: "black", // default border
                              },
                              "&:hover fieldset": {
                                borderColor: "#C10E14", // hover border
                              },
                              "&.Mui-focused fieldset": {
                                borderColor: "#C10E14", // border on focus
                              },
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Button
                          type="submit"
                          variant="contained"
                          size="large"
                          fullWidth
                          sx={{
                            borderRadius: "50px",
                            py: 1.5,
                            fontSize: "1rem",
                            boxShadow: "0 4px 14px rgba(0, 0, 0, 0.15)",
                            backgroundColor: "#C10E14", // Set the background color to #C10E14
                            "&:hover": {
                              backgroundColor: "#A30B11", // Set the hover background color to #A30B11
                            },
                          }}
                        >
                          Submit Message
                        </Button>
                      </Grid>
                    </Grid>
                  </ContactFormBox>
                </motion.div>
              </Grid>
              <Grid item xs={12} md={6}>
                <motion.div
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8 }}
                >
                  <ContactInfoBox>
                    <Typography
                      variant="h5"
                      component="h3"
                      gutterBottom
                      fontWeight={600}
                    >
                      Contact Information
                    </Typography>
                    <Box sx={{ mt: 4 }}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", mb: 3 }}
                      >
                        <IconWrapper>
                          <MdAddCall />
                        </IconWrapper>
                        <Box>
                          <Typography variant="body1" fontWeight={500}>
                            Phone
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            +971 4 576 0110
                          </Typography>
                        </Box>
                      </Box>
                      <Box
                        sx={{ display: "flex", alignItems: "center", mb: 3 }}
                      >
                        <IconWrapper>
                          <MdEmail />
                        </IconWrapper>
                        <Box>
                          <Typography variant="body1" fontWeight={500}>
                            Email
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            info@descorealestate.com
                          </Typography>
                        </Box>
                      </Box>
                      <Box
                        sx={{ display: "flex", alignItems: "center", mb: 3 }}
                      >
                        <IconWrapper>
                          <MdLocationOn />
                        </IconWrapper>
                        <Box>
                          <Typography variant="body1" fontWeight={500}>
                            Location
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Office 10C-08, I-Rise Tower, Barsha Heights, Dubai,
                            UAE
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                    <Box sx={{ mt: 4 }}>
                      <Typography variant="h6" gutterBottom fontWeight={600}>
                        Office Hours
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Monday - Saturday: 9:00 AM - 6:00 PM
                      </Typography>
                      {/* <Typography variant="body2" color="text.secondary">
                      Saturday: 10:00 AM - 4:00 PM
                    </Typography> */}
                      <Typography variant="body2" color="text.secondary">
                        Sunday: Closed
                      </Typography>
                    </Box>
                  </ContactInfoBox>
                </motion.div>
              </Grid>
            </Grid>
          </Container>
        </PageSection>

        {/* Location Image Section */}
        <PageSection sx={{ bgcolor: "#fff" }}>
          <Container>
            <Grid container spacing={6} alignItems="center">
              <Grid item xs={12} md={6}>
                <motion.div
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8 }}
                >
                  <Typography
                    variant="h4"
                    component="h2"
                    gutterBottom
                    fontWeight={700}
                  >
                    Our Location
                  </Typography>
                  <Typography variant="body1" paragraph>
                    Visit our office to discuss your real estate needs in
                    person. Our team of experts is ready to provide personalized
                    advice and solutions tailored to your requirements.
                  </Typography>
                  <Typography variant="body1" paragraph>
                    We're conveniently located in the heart of Dubai, easily
                    accessible from all major areas of the city.
                  </Typography>
                  <Box sx={{ mt: 4 }}>
                    <a href="https://www.google.com/maps/place/I+Rise+Tower+-+Hessa+Street+-+Barsha+Heights+-+Dubai+-+UAE/@25.0972538,55.1782886,17z/data=!3m1!4b1!4m6!3m5!1s0x3e5f6bb088ca6bb7:0x7ea961b8ac7b6af6!8m2!3d25.097249!4d55.1808635!16s%2Fg%2F11sdq3r72d?entry=ttu&g_ep=EgoyMDI1MDQyMy4wIKXMDSoASAFQAw%3D%3D">
                      {" "}
                      <Button
                        variant="outlined"
                        size="large"
                        sx={{
                          borderRadius: "50px",
                          py: 1,
                          px: 3,
                          backgroundColor: "white",
                          color: "#C10E14",
                          border: "2px solid #C10E14",
                          boxShadow: "none",
                          "&:hover": {
                            backgroundColor: "#C10E14",
                            color: "white",
                            boxShadow: "none",
                          },
                          "&:focus": {
                            outline: "none",
                          },
                          "&.Mui-focusVisible": {
                            outline: "none",
                          },
                        }}
                      >
                        Get Directions
                      </Button>
                    </a>
                  </Box>
                </motion.div>
              </Grid>
              <Grid item xs={12} md={6}>
                <motion.div
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8 }}
                >
                  <ContactImage src={contactBg} alt="DESCO Office Location" />
                </motion.div>
              </Grid>
            </Grid>
          </Container>
        </PageSection>
      </Box>
    </>
  );
};

export default Contact;
